import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import '../../css/Pages.css';
import { downloadExcel } from '../../utils/excelUtils';

const HRStatistics = () => {
    const toast = useRef(null);

    const [departments, setDepartments] = useState([]);
    const [columns, setColumns] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [filterDept, setFilterDept] = useState('');
    const [filterTitle, setFilterTitle] = useState('');
    const [filterDegree, setFilterDegree] = useState('');

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [previewDisplayColumns, setPreviewDisplayColumns] = useState([]);
    const [exportFilters, setExportFilters] = useState({
        department: '',
        title: '',
        degree: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = useCallback(() => {
        return {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
        };
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                const API_BASE = process.env.REACT_APP_API_BASE_URL;

                const [deptRes, titleRes, degreeRes, userRes] = await Promise.all([
                    fetch(`${API_BASE}/department`, { headers: getAuthHeaders() }),
                    fetch(`${API_BASE}/jobtitle`, { headers: getAuthHeaders() }),
                    fetch(`${API_BASE}/educationlevel`, { headers: getAuthHeaders() }),
                    fetch(`${API_BASE}/user?mode=statistics`, { headers: getAuthHeaders() })
                ]);

                if (deptRes.ok && titleRes.ok && degreeRes.ok && userRes.ok) {
                    const depts = await deptRes.json();
                    const titles = await titleRes.json();
                    const degrees = await degreeRes.json();
                    const userList = await userRes.json();

                    setDepartments(depts);
                    setUsers(userList);

                    const combinedColumns = [
                        ...titles.map(t => ({ id: t.Id, name: t.Name, type: 'Title' })),
                        ...degrees.map(d => ({ id: d.Id, name: d.Name, type: 'Degree' }))
                    ];
                    setColumns(combinedColumns);
                }
            } catch (error) {
                console.error("Lỗi tải dữ liệu thống kê:", error);
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu thống kê!', life: 3000 });
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [getAuthHeaders]);

    const generateStatisticsData = useCallback((targetFilterDept) => {
        let stats = [];

        let allowedDeptIds = [];
        if (targetFilterDept) {
            const selectedDeptId = targetFilterDept.toString();
            allowedDeptIds.push(selectedDeptId);

            const findChildren = (parentId) => {
                const children = departments.filter(d => d.ParentId?.toString() === parentId.toString());
                children.forEach(child => {
                    const childId = child.Id.toString();
                    if (!allowedDeptIds.includes(childId)) {
                        allowedDeptIds.push(childId);
                        findChildren(childId);
                    }
                });
            };

            findChildren(selectedDeptId);
        }

        const filteredDepts = targetFilterDept
            ? departments.filter(d => allowedDeptIds.includes(d.Id.toString()))
            : departments;

        filteredDepts.forEach((dept, index) => {
            let row = {
                index: index + 1,
                deptId: dept.Id,
                deptName: dept.Name
            };

            const usersInDept = users.filter(u => u.DepartmentId === dept.Id);

            columns.forEach(col => {
                if (col.type === 'Title') {
                    row[col.name] = usersInDept.filter(u => u.AcademicTitleId === col.id).length;
                } else if (col.type === 'Degree') {
                    row[col.name] = usersInDept.filter(u => u.DegreeId === col.id).length;
                }
            });
            stats.push(row);
        });

        let totalRow = { index: '', deptId: 'total', deptName: 'Tổng cộng' };
        columns.forEach(col => {
            totalRow[col.name] = stats.reduce((sum, row) => sum + row[col.name], 0);
        });

        return [totalRow, ...stats];
    }, [departments, users, columns]);

    const tableData = useMemo(() => generateStatisticsData(filterDept), [generateStatisticsData, filterDept]);

    const displayColumns = useMemo(() => {
        return columns.filter(col => {
            if (filterTitle && col.type === 'Title' && col.id.toString() !== filterTitle.toString()) return false;
            if (filterDegree && col.type === 'Degree' && col.id.toString() !== filterDegree.toString()) return false;
            return true;
        });
    }, [columns, filterTitle, filterDegree]);

    const executeExport = useCallback((dataToExport, columnsToExport, fileSuffix) => {
        if (!dataToExport || dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const summaryRaw = dataToExport[0];
        const mainData = dataToExport.slice(1);

        const formattedData = mainData.map((row) => {
            let formattedRow = {
                'STT': row.index,
                'Đơn vị': row.deptName
            };
            columnsToExport.forEach(col => {
                formattedRow[col.name.toUpperCase()] = row[col.name] || 0;
            });
            return formattedRow;
        });

        let summaryRow = {
            'STT': summaryRaw.index,
            'Đơn vị': summaryRaw.deptName
        };
        columnsToExport.forEach(col => {
            summaryRow[col.name.toUpperCase()] = summaryRaw[col.name] || 0;
        });

        const colWidths = [
            { wch: 8 },
            { wch: 40 },
            ...columnsToExport.map(() => ({ wch: 15 }))
        ];

        downloadExcel({
            data: formattedData,
            fileName: `ThongKeNguonLucKH_${fileSuffix}`,
            sheetName: 'ThongKeNguonLuc',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(tableData, displayColumns, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getAdvancedDisplayColumns = useCallback(() => {
        return columns.filter(col => {
            if (exportFilters.title && col.type === 'Title' && col.id.toString() !== exportFilters.title.toString()) return false;
            if (exportFilters.degree && col.type === 'Degree' && col.id.toString() !== exportFilters.degree.toString()) return false;
            return true;
        });
    }, [columns, exportFilters]);

    const handlePreviewAdvancedFilters = () => {
        const advancedData = generateStatisticsData(exportFilters.department);
        const advancedCols = getAdvancedDisplayColumns();
        setPreviewExportData(advancedData);
        setPreviewDisplayColumns(advancedCols);
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ department: '', title: '', degree: '' });
        setPreviewExportData(null);
        setPreviewDisplayColumns([]);
    };

    const handleExportFromModal = () => {
        const dataToExport = previewExportData || generateStatisticsData(exportFilters.department);
        const colsToExport = previewDisplayColumns.length > 0 ? previewDisplayColumns : getAdvancedDisplayColumns();

        executeExport(dataToExport, colsToExport, 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const deptColumnWidth = isDesktop ? '250px' : '140px';
    const dataColumnWidth = isDesktop ? '130px' : '100px';

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Id}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container" style={{ padding: '20px', paddingBottom: '90px' }}>
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>THỐNG KÊ NGUỒN LỰC KHOA HỌC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <select className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
                        <option value="">Toàn bộ đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <select className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filterTitle} onChange={(e) => setFilterTitle(e.target.value)}>
                        <option value="">Toàn bộ chức danh</option>
                        {columns.filter(c => c.type === 'Title').map(t => <option key={`t-${t.id}`} value={t.id}>{t.name}</option>)}
                    </select>

                    <select className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filterDegree} onChange={(e) => setFilterDegree(e.target.value)}>
                        <option value="">Toàn bộ trình độ</option>
                        {columns.filter(c => c.type === 'Degree').map(d => <option key={`d-${d.id}`} value={d.id}>{d.name}</option>)}
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i> Đang tải dữ liệu...
                </div>
            ) : (
                <div style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <table className="custom-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '100%', margin: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ backgroundColor: '#f4f6f9', borderBottom: '2px solid #ddd' }}>
                                <th style={{ ...thStyle, minWidth: '40px', width: '40px' }}>STT</th>

                                <th style={{
                                    ...thStyle,
                                    minWidth: deptColumnWidth,
                                    maxWidth: deptColumnWidth,
                                    width: deptColumnWidth,
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: '#f4f6f9',
                                    zIndex: 3,
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word'
                                }}>
                                    ĐƠN VỊ
                                </th>

                                {displayColumns.map(col => (
                                    <th key={`${col.type}-${col.id}`} style={{ ...thStyle, minWidth: dataColumnWidth, width: dataColumnWidth }}>
                                        {col.name.toUpperCase()}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row) => (
                                <tr key={row.deptId} style={{ borderBottom: '1px solid #eee', backgroundColor: row.deptId === 'total' ? '#fdf8e4' : 'transparent', fontWeight: row.deptId === 'total' ? 'bold' : 'normal' }}>

                                    <td style={{ ...tdStyle, minWidth: '40px', width: '40px' }}>{row.index}</td>

                                    <td style={{
                                        ...tdStyle,
                                        minWidth: deptColumnWidth,
                                        maxWidth: deptColumnWidth,
                                        width: deptColumnWidth,
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: row.deptId === 'total' ? '#fdf8e4' : '#fff',
                                        zIndex: 1,
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word'
                                    }}>
                                        {row.deptName}
                                    </td>

                                    {displayColumns.map(col => (
                                        <td key={`${row.deptId}-${col.name}`} style={{ ...tdStyle, textAlign: 'center', color: row[col.name] > 0 ? '#1a56db' : '#888', minWidth: dataColumnWidth, width: dataColumnWidth }}>
                                            {row[col.name]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>

                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Đơn vị</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.department} onChange={(e) => setExportFilters({ ...exportFilters, department: e.target.value })}>
                                        <option value="">Toàn bộ đơn vị</option>
                                        {renderDeptTree()}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Chức danh</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.title} onChange={(e) => setExportFilters({ ...exportFilters, title: e.target.value })}>
                                        <option value="">Toàn bộ chức danh</option>
                                        {columns.filter(c => c.type === 'Title').map(t => <option key={`exp-t-${t.id}`} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Trình độ</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.degree} onChange={(e) => setExportFilters({ ...exportFilters, degree: e.target.value })}>
                                        <option value="">Toàn bộ trình độ</option>
                                        {columns.filter(c => c.type === 'Degree').map(d => <option key={`exp-d-${d.id}`} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước cấu trúc bảng xuất:
                                        ({previewExportData.length - 1} Đơn vị, {previewDisplayColumns.length} Cột dữ liệu)
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '100%', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>
                                                <tr>
                                                    <th style={{ textAlign: 'center', minWidth: '40px', width: '40px' }}>STT</th>

                                                    <th style={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        backgroundColor: '#f8f9fa',
                                                        zIndex: 3,
                                                        minWidth: deptColumnWidth,
                                                        maxWidth: deptColumnWidth,
                                                        width: deptColumnWidth,
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word'
                                                    }}>ĐƠN VỊ</th>

                                                    {previewDisplayColumns.map(col => (
                                                        <th key={`preview-th-${col.id}`} style={{ textAlign: 'center', minWidth: dataColumnWidth, width: dataColumnWidth }}>
                                                            {col.name.toUpperCase()}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((row) => (
                                                        <tr key={`preview-${row.deptId}`} style={{ backgroundColor: row.deptId === 'total' ? '#fdf8e4' : 'transparent', fontWeight: row.deptId === 'total' ? 'bold' : 'normal' }}>
                                                            <td style={{ textAlign: 'center', minWidth: '40px', width: '40px' }}>{row.index}</td>

                                                            <td style={{
                                                                position: 'sticky',
                                                                left: 0,
                                                                backgroundColor: row.deptId === 'total' ? '#fdf8e4' : '#fff',
                                                                zIndex: 1,
                                                                minWidth: deptColumnWidth,
                                                                maxWidth: deptColumnWidth,
                                                                width: deptColumnWidth,
                                                                whiteSpace: 'normal',
                                                                wordWrap: 'break-word'
                                                            }}>
                                                                {row.deptName}
                                                            </td>

                                                            {previewDisplayColumns.map(col => (
                                                                <td key={`preview-td-${row.deptId}-${col.id}`} style={{ textAlign: 'center', color: row[col.name] > 0 ? '#1a56db' : '#888', minWidth: dataColumnWidth, width: dataColumnWidth }}>
                                                                    {row[col.name]}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={previewDisplayColumns.length + 2} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                            Không có dữ liệu
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const thStyle = { padding: '12px 10px', textAlign: 'center', color: '#1a56db', fontSize: '13px' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#333' };

export default HRStatistics;